// Shared sermon-series grouping + display-title helpers (Sermons tab rebuild).
//
// groupSermonsBySeries was lifted out of BibleApp.jsx's church-profile
// sermons view (the "group by playlist, fall back to series" logic that
// already worked there) so the new Sermons tab's Series section and that
// existing view share one implementation instead of drifting apart.

// A sermon can belong to several playlists (uploaded once, shown under each).
// Falls back to the single free-text `series` column, then "Standalone
// sermons", for legacy/in-app-uploaded rows with no playlists array.
export function groupSermonsBySeries(sermons) {
  const seriesMap = new Map();
  for (const s of sermons || []) {
    const keys = (Array.isArray(s.playlists) && s.playlists.length)
      ? [...new Set(s.playlists.map(p => (p || "").trim()).filter(Boolean))]
      : [(s.series && s.series.trim()) || "Standalone sermons"];
    for (const key of keys) {
      if (!seriesMap.has(key)) seriesMap.set(key, []);
      seriesMap.get(key).push(s);
    }
  }
  // Sort each series newest-first, then sort the series list itself by
  // most-recent sermon date (active series float to the top).
  return [...seriesMap.entries()]
    .map(([name, arr]) => {
      const sorted = [...arr].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      return { name, sermons: sorted, thumb: seriesThumbOf(sorted) };
    })
    .sort((a, b) => (b.sermons[0]?.date || "").localeCompare(a.sermons[0]?.date || ""));
}

// Representative thumbnail for a series — newest sermon with a youtube_id.
function seriesThumbOf(arr) {
  const withYt = (arr || []).find(s => s.youtube_id);
  return withYt ? `https://i.ytimg.com/vi/${withYt.youtube_id}/mqdefault.jpg` : null;
}

// Same grouping as groupSermonsBySeries, but keyed by church_id + name —
// needed wherever sermons from several churches are grouped together
// (docs/FIX-SERMONS-POLISH.md §3's multi-church Series carousel). Keying by
// name alone would wrongly merge different churches' same-named series, or
// merge every church's series-less sermons into one shared "Standalone
// sermons" group.
export function groupSermonsBySeriesByChurch(sermons) {
  const seriesMap = new Map();
  for (const s of sermons || []) {
    const keys = (Array.isArray(s.playlists) && s.playlists.length)
      ? [...new Set(s.playlists.map(p => (p || "").trim()).filter(Boolean))]
      : [(s.series && s.series.trim()) || "Standalone sermons"];
    for (const name of keys) {
      const key = `${s.church_id}::${name}`;
      if (!seriesMap.has(key)) seriesMap.set(key, { name, churchId: s.church_id, arr: [] });
      seriesMap.get(key).arr.push(s);
    }
  }
  return [...seriesMap.values()]
    .map(({ name, churchId, arr }) => {
      const sorted = [...arr].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      return { name, churchId, sermons: sorted, thumb: seriesThumbOf(sorted) };
    })
    .sort((a, b) => (b.sermons[0]?.date || "").localeCompare(a.sermons[0]?.date || ""));
}

export function firstPassageOf(sermon) {
  const r = (sermon?.sermon_refs || [])[0];
  return r ? `${r.book} ${r.chapter}:${r.verse}` : "";
}

// Always the short AI-generated/elder-edited display_title. Never a Bible
// reference in the title position (that belongs in the meta line, e.g.
// SundaySermonCard's "[passage] · [date] · [preacher]") — falls back to the
// raw title, then a generic label, rather than ever rendering a reference
// or going blank.
export function displayTitleOf(sermon) {
  if (!sermon) return "";
  return sermon.display_title || sermon.title || "Sunday sermon";
}
